import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import SearchIcon from "src/core/assets/images/icons/search.svg?react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "src/core/components/ui/command";
import { Input } from "src/core/components/ui/input";
import type { SearchCustomerFolderByStringQuery } from "src/core/gql/graphql";
import { useClickOutside } from "src/core/hooks/common/useClickOutside";
import { useSearchFolders } from "src/core/hooks/useSearchFolders";
import { cn } from "src/core/lib/utils";
import { formatIsoToDate } from "src/core/utils/date";

type SearchFolderProps = {
  onSelect?: (folderId: string) => void;
  className?: string;
  disabled?: boolean;
};

type QueryData = SearchCustomerFolderByStringQuery;
type Summary = NonNullable<NonNullable<QueryData["customerFoldersPage"]>["summaries"]>[number];

type FieldCode = "name" | "registration" | "firstName" | "lastName" | "birthDate";

type FieldedStringValue = {
  inputField?: { fieldCode?: string | null } | null;
  stringValue?: string | null;
};

const highlightText = (text: string, searchQuery: string): ReactNode[] => {
  const q = searchQuery.trim();
  if (!q) return [text];

  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${escaped})`, "i");

  const parts = text.split(re);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} data-testid="search-highlight" className="font-medium">
        {part}
      </span>
    ) : (
      part
    )
  );
};

const byField =
  (code: FieldCode) =>
  (input: FieldedStringValue): boolean =>
    input.inputField?.fieldCode === code;

const getStringValue = (
  inputs: readonly FieldedStringValue[] | null | undefined,
  code: FieldCode
) => inputs?.find(byField(code))?.stringValue ?? null;

const getFolderFileSummary = (summary: Summary) =>
  summary.customerFolder.folderFiles.summaries?.[0] ?? null;

const getPersonDisplayName = (summary: Summary): string => {
  const folderFile = getFolderFileSummary(summary);
  const stakeholder = folderFile?.currentCustomerFolder?.stakeholders?.[0];

  const firstName =
    getStringValue(
      stakeholder?.inputValues as FieldedStringValue[] | null | undefined,
      "firstName"
    ) ?? "";
  const lastName =
    getStringValue(
      stakeholder?.inputValues as FieldedStringValue[] | null | undefined,
      "lastName"
    ) ?? "";
  const birthDate = getStringValue(
    stakeholder?.inputValues as FieldedStringValue[] | null | undefined,
    "birthDate"
  );

  const fullName = `${firstName} ${lastName}`.trim();
  if (!fullName) return "";

  return birthDate ? `${fullName} - ${formatIsoToDate(birthDate)}` : fullName;
};

const getCompanyDisplayName = (summary: Summary, fallbackNoName: string): string => {
  const name =
    getStringValue(
      summary.customer.inputValues as FieldedStringValue[] | null | undefined,
      "name"
    ) ?? "";
  const registration = getStringValue(
    summary.customer.inputValues as FieldedStringValue[] | null | undefined,
    "registration"
  );

  const base = name.trim() ? name : fallbackNoName;
  return registration ? `${base} (${registration})` : base;
};

export function SearchFolder({ onSelect, className, disabled = false }: SearchFolderProps) {
  const { t } = useTranslation();
  const {
    searchQuery,
    data,
    loading,
    hasResults,
    shouldShowDropdown,
    handleSearch,
    handleSelect,
    handleClose,
  } = useSearchFolders();

  const searchRef = useClickOutside<HTMLDivElement>(handleClose);

  return (
    <div
      ref={searchRef}
      data-testid="search-folder-root"
      className={cn("relative w-[500px]", className)}
    >
      <div className="relative">
        <SearchIcon
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 h-6 w-6 -translate-y-1/2 transform text-muted-foreground"
        />
        <Input
          aria-label={t("search.placeholder")}
          placeholder={t("search.placeholder")}
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full bg-gray-100 pl-12 pr-4"
          disabled={disabled}
        />
      </div>

      {shouldShowDropdown && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1">
          <Command className="rounded-lg border bg-gray-xlight">
            <CommandList className="max-h-[200px] overflow-auto">
              {loading && <CommandEmpty>{t("search.loading")}</CommandEmpty>}

              {!loading && !hasResults && <CommandEmpty>{t("search.noResults")}</CommandEmpty>}

              {!loading && hasResults && (
                <CommandGroup>
                  {data?.customerFoldersPage.summaries?.map((summary) => {
                    const folderFile = getFolderFileSummary(summary);
                    const id = folderFile?.id;

                    // Si on n'a pas d'id, on n'affiche pas l'item (sinon sélection impossible).
                    if (!id) return null;

                    const isPerson = summary.customer.isPerson;

                    const clientName = isPerson
                      ? getPersonDisplayName(summary) || t("search.noName")
                      : getCompanyDisplayName(summary, t("search.noName"));

                    return (
                      <CommandItem
                        key={id}
                        data-testid={`search-item-${id}`}
                        value={clientName}
                        className="cursor-pointer"
                        onSelect={() => handleSelect(id, onSelect)}
                      >
                        <span data-testid="search-item-name">
                          {highlightText(clientName, searchQuery)}
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}
